/**
 * The single source of truth for which DOM attributes Heap must redact.
 *
 * Heap's `data-heap-redact-attributes` takes a comma-joined list of attribute
 * names whose values are replaced with `***` before they leave the browser.
 * BOTH this attribute and `data-heap-redact-text` cascade to descendants, so a
 * single marking on a redaction root covers the whole subtree below it.
 *
 * This constant was previously hand-written as a string literal in 4+ places
 * (the AuthenticatedLayout shell root + each portal dialog root) with drifting
 * attribute order. Centralizing it here gives one auditable list:
 *  - `value`       — `<input>` / `<textarea>` values (prices, EMD, names, zips).
 *  - `title`       — tooltip text that can echo PII.
 *  - `alt`         — image alt text (property thumbnails use the street address).
 *  - `aria-label`  — accessible labels that can echo PII.
 *  - `href`        — `<a>` targets (mailto: links, presigned contract URLs).
 *
 * NOTE: Heap cannot redact the `id` attribute this way (use `heap-ignore`).
 *
 * Source: https://developers.heap.io/docs/hide-sensitive-data
 */

/** Attribute names Heap must redact, as a readonly tuple. */
export const REDACT_ATTRS_LIST = ['value', 'title', 'alt', 'aria-label', 'href'] as const

/**
 * The comma-joined string for the `data-heap-redact-attributes` DOM attribute.
 * Apply this at every redaction root (the authenticated-layout shell + each
 * portal dialog/drawer content root) alongside `data-heap-redact-text="true"`.
 */
export const REDACT_ATTRS = REDACT_ATTRS_LIST.join(',')
